/**
 * Optional HEXACO prod/staging API switch (single place to delete later).
 *
 * Docs: doc/setup_for_testing_hexaco_payment_with_staging.md
 *
 * Disable in a production build without code deletion:
 *   REACT_APP_HEXACO_API_ENV_SWITCH=false
 *
 * When disabled: always uses REACT_APP_API_URL / REACT_APP_TURNSTILE_SITE_KEY /
 * REACT_APP_API_AUTHORIZATION_APP_CODE; ignores ?api= and localStorage.
 * Localhost primary URL still resolves APPCODE from the staging env var.
 *
 * Call sites to revert when removing the feature entirely:
 * - src/services/hexacoWebApi.ts (getHexacoApiBaseUrl, getHexacoApiAppCode)
 * - src/components/hexaco/TurnstileWidget.tsx (getHexacoTurnstileSiteKey)
 * - src/App.tsx (HexacoApiEnvSync effect)
 */

export type HexacoApiEnv = "prod" | "staging";

export const HEXACO_API_ENV_STORAGE_KEY = "hexacoApiEnv";

/** Mirrors keys in hexacoWebApi — keep in sync if those keys change. */
const HEXACO_SESSION_ORDER_ID_KEY = "hexacoWebOrderId";
const HEXACO_SESSION_ACCESS_TOKEN_KEY = "hexacoWebAccessToken";
const HEXACO_SESSION_CUSTOMER_NAME_KEY = "hexacoWebCustomerName";

/** Set to "false" to strip runtime staging switch from a build. Default: enabled. */
export const isHexacoApiEnvSwitchEnabled =
  process.env.REACT_APP_HEXACO_API_ENV_SWITCH !== "false";

const PROD_API_URL_DEFAULT = "https://api.deepsoulai.com";
const STAGING_API_URL_DEFAULT = "https://apiuat.deepsoulai.com";

export const clearHexacoSessionForApiEnvChange = (): void => {
  try {
    sessionStorage.removeItem(HEXACO_SESSION_ORDER_ID_KEY);
    sessionStorage.removeItem(HEXACO_SESSION_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(HEXACO_SESSION_CUSTOMER_NAME_KEY);
  } catch {
    // Ignore.
  }
};

export const getHexacoApiEnv = (): HexacoApiEnv => {
  if (!isHexacoApiEnvSwitchEnabled) {
    return "prod";
  }

  try {
    const stored = localStorage.getItem(HEXACO_API_ENV_STORAGE_KEY);
    return stored === "staging" ? "staging" : "prod";
  } catch {
    return "prod";
  }
};

/** Persist env. Returns true when the value changed. No-op when switch is disabled. */
export const setHexacoApiEnv = (env: HexacoApiEnv): boolean => {
  if (!isHexacoApiEnvSwitchEnabled) {
    return false;
  }

  const previous = getHexacoApiEnv();
  if (previous === env) {
    return false;
  }

  try {
    localStorage.setItem(HEXACO_API_ENV_STORAGE_KEY, env);
  } catch {
    // Ignore quota / private-mode failures.
  }

  return true;
};

export const parseHexacoApiEnvParam = (
  value: string | null | undefined,
): HexacoApiEnv | null => {
  if (value === "staging") return "staging";
  if (value === "prod" || value === "production") return "prod";
  return null;
};

export const getHexacoApiBaseUrl = (): string => {
  if (isHexacoApiEnvSwitchEnabled && getHexacoApiEnv() === "staging") {
    return (
      process.env.REACT_APP_API_URL_STAGING || STAGING_API_URL_DEFAULT
    ).replace(/\/$/, "");
  }

  return (process.env.REACT_APP_API_URL || PROD_API_URL_DEFAULT).replace(
    /\/$/,
    "",
  );
};

export const getHexacoTurnstileSiteKey = (): string => {
  if (isHexacoApiEnvSwitchEnabled && getHexacoApiEnv() === "staging") {
    return process.env.REACT_APP_TURNSTILE_SITE_KEY_STAGING || "";
  }

  return process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
};

const isLocalhostApiUrl = (url: string): boolean =>
  /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(url);

/** Alibaba API Gateway APPCODE. Staging switch or localhost primary → staging code. */
export const getHexacoApiAppCode = (): string => {
  if (isHexacoApiEnvSwitchEnabled && getHexacoApiEnv() === "staging") {
    return process.env.REACT_APP_API_AUTHORIZATION_APP_CODE_STAGING || "";
  }

  const primaryUrl = process.env.REACT_APP_API_URL || "";
  if (isLocalhostApiUrl(primaryUrl)) {
    return process.env.REACT_APP_API_AUTHORIZATION_APP_CODE_STAGING || "";
  }

  return process.env.REACT_APP_API_AUTHORIZATION_APP_CODE || "";
};

/**
 * Apply `?api=staging|prod` from the current search string.
 * Strips the `api` param from the returned search when present.
 * When the switch is disabled, still strips `api` but never changes env.
 */
export const applyHexacoApiEnvFromSearch = (
  search: string,
): {
  env: HexacoApiEnv;
  changed: boolean;
  didConsumeParam: boolean;
  nextSearch: string;
} => {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const parsed = parseHexacoApiEnvParam(params.get("api"));

  if (!parsed) {
    return {
      env: getHexacoApiEnv(),
      changed: false,
      didConsumeParam: false,
      nextSearch: search,
    };
  }

  params.delete("api");
  const qs = params.toString();
  const nextSearch = qs ? `?${qs}` : "";

  if (!isHexacoApiEnvSwitchEnabled) {
    return {
      env: "prod",
      changed: false,
      didConsumeParam: true,
      nextSearch,
    };
  }

  const changed = setHexacoApiEnv(parsed);

  return {
    env: parsed,
    changed,
    didConsumeParam: true,
    nextSearch,
  };
};
